import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Button,
  Checkbox,
  getColor,
  Icons,
  InputField,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { useNavigate } from "react-router-dom";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import useAdminLoginStore from "../../redux/dispatcher/useAdminLoginStore";
import FindPasswordModal from "./FindPasswordModal";
import GoogleOTPRegisterModal from "./GoogleOTPRegisterModal";
import MailOTPLoginModal from "./MailOTPLoginModal";
import GoogleOTPLoginModal from "./GoogleOTPLoginModal";
import { getRedirectUrl, removeRedirectUrl } from "../../shared/utils/auth";
import { useTranslation } from "react-i18next";
import ChangePasswordModal from "./ChangePasswordModal";
import { nanoid } from "@reduxjs/toolkit";
import currentUrlUtils from "../../shared/utils/currentUrlUtils";
import ESETLoginModal from "./ESETLoginModal";
import classNames from "classnames";
import i18n from "../../locales/i18n";
const bgLoginImagePath =
  window.origin === "http://localhost:3000" ||
  window.origin === "http://127.0.0.1:3000"
    ? "https://fasoo.wrapsodyeco.com/eco-gateway/resources/images/login_sub_img.png"
    : window.origin + "/eco-gateway/resources/images/login_sub_img.png";

const companyLogoPath =
  window.origin === "http://localhost:3000" ||
  window.origin === "http://127.0.0.1:3000"
    ? "https://fasoo.wrapsodyeco.com/eco-gateway/resources/images/login_bi_img.png"
    : window.origin + "/eco-gateway/resources/images/login_bi_img.png";

interface LoginProps {
  isAdmin?: boolean;
  type?: "LOGIN" | "PASSWORD_INIT";
  changeLoading: (status: boolean) => void;
}

const Login: React.FC<LoginProps> = ({
  isAdmin = false,
  type = "LOGIN",
  changeLoading,
}) => {
  /** hook 정리 **/
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toasts = useToast();
  const { sessionNew, loginSession } = useLoginStore();
  const { adminSessionNew, adminSessionLogin } = useAdminLoginStore();
  const { toastMsg } = currentUrlUtils.getAllFirstQuery();

  /** State 정리 **/
  const [userInputInfo, changeUserInputInfo] = useState({
    userId:
      localStorage.getItem("savedUserId") ??
      (document.getElementById("userId") as any)?.value ??
      "",
    password: (document.getElementById("password") as any)?.value ?? "",
  }); // 사용자 정보 입력 정보
  const [isPwVisible, togglePWVisible] = useState(false); // 비밀번호 보임, 안보임 처리용
  const [err, changeErr] = useState("NONE");
  const [saveId, changeSaveId] = useState(
    localStorage.getItem("savedUserId") ? true : false
  ); // 로그인 상태 유지
  const [extraOpenModalInfo, changeExtraOpenModalInfo] = useState(
    type === "PASSWORD_INIT" ? "CHANGE_PW" : "CLOSE"
  );
  const [pwUserId, changePWUserId] = useState<string | undefined>(undefined); // 관리자 페이지에서 비밀번호 변경시 아이디 저장

  /** useEffect 정리 */
  useEffect(() => {
    if (toastMsg) {
      toasts.toastMsg(nanoid(), t(toastMsg), "success");
    }
  }, []);
  /** 함수 정리 **/
  /** id와 password 를 통한 로그인 */
  const handleLogin = async () => {
    if (!userInputInfo.userId || !userInputInfo.password) return;
    /** 에러: 비밀번호가 유효하지 않음. */
    // if (!utilsCommon.passwordValidation(userInputInfo.password)) {
    //   changeErr("INVALID_PW_TYPE");
    //   return;
    // }
    const otp: any = isAdmin ? await adminSessionNew() : await sessionNew();
    if (otp.payload) {
      const resp: any = isAdmin
        ? await adminSessionLogin({
            userId: userInputInfo.userId,
            userPwd: userInputInfo.password,
            otp: otp.payload.apiToken,
          })
        : await loginSession({
            userId: userInputInfo.userId,
            userPwd: userInputInfo.password,
            otp: otp.payload.apiToken,
          });
      if (resp.error && resp.error.message) {
        if (resp.error.name !== "INVALID_USER_ID_PWD") {
          if (resp?.error?.message.includes("MAIL_OTP")) {
            changeExtraOpenModalInfo("MAIL_OTP");
          } else if (resp?.error?.message?.includes("GOOGLE_OTP")) {
            changeExtraOpenModalInfo("GOOGLE_OTP_REGISTER");
          } else if (resp?.error?.name === "NOT_ALLOW_IP") {
            navigate(`${process.env.PUBLIC_URL}/error?errorCode=NOT_ALLOW_IP`);
          } else if (resp.error?.name === "INVALID_TENANCY") {
            navigate("/error?errorCode=INVALID_TENANCY");
          } else if (resp.error?.name === "SHOULD_RENEW_PASSWORD") {
            changePWUserId(userInputInfo.userId);
            changeExtraOpenModalInfo("CHANGE_PW");
          } else if (resp.error?.name?.includes("ESET_OTP")) {
            changeExtraOpenModalInfo("ESET_LOGIN");
          } else if (resp.error?.name === "EXCEED_PASSWORD_ERROR_LIMIT") {
            toasts.toastMsg(
              uuidv4(),
              t("login.login.error.passwordErrorLimit"),
              "error"
            );
          } else {
            toasts.toastMsg(uuidv4(), resp.error.message, "error");
          }
        } else {
          changeErr("INVALID_USER_ID_PWD");
        }
        return;
      }
      changeErr("NONE");
      if (resp?.payload?.multiFactor === "MAIL_OTP") {
        changeExtraOpenModalInfo("MAIL_OTP");
      } else if (resp?.payload?.multiFactor === "GOOGLE_OTP") {
        changeExtraOpenModalInfo("GOOGLE_OTP");
      } else if (resp.payload?.multiFactor === "ESET_OTP") {
        changeExtraOpenModalInfo("ESET_LOGIN");
      } else if (resp.payload) {
        if (saveId) {
          localStorage.setItem("savedUserId", userInputInfo.userId);
        } else {
          localStorage.removeItem("savedUserId");
        }
        let redirectUrl = getRedirectUrl();
        if (redirectUrl && !redirectUrl.includes("/login")) {
          removeRedirectUrl();
          try {
            const url = new URL(redirectUrl);
            navigate(url.pathname);
          } catch (err) {
            window.location.replace(redirectUrl);
          }
        } else {
          const defaultTenancy = resp.payload?.defaultTenancy ?? "My Space";
          navigate(
            isAdmin
              ? `${process.env.PUBLIC_URL}/admin/home`
              : window.eco.serviceType === "OCI" && defaultTenancy
              ? `${process.env.PUBLIC_URL}/${defaultTenancy}/home`
              : `${process.env.PUBLIC_URL}/home`
          );
        }
      }
    }
  };
  /** 비밀번호 입력창 에러메세지 정리 */
  const renderPWErrorStr = (type: string) => {
    switch (type) {
      case "INVALID_PW_TYPE":
        return t("login.login.error.passwordRegex");
      case "INVALID_USER_ID_PWD":
        return t("login.login.error.invalidUser");
    }
    return undefined;
  };

  return (
    <Box width={"100%"} height={"100%"} classes={"login"}>
      <Box
        height={"100%"}
        alignItems="center"
        justifyContent={"center"}
        classes={"login-left"}
      >
        <Box direction={"column"} alignItems={"center"}>
          <img src={companyLogoPath} alt={"logo"} height={56} width={234} />
          {window.eco.serviceType === "OCI" && (
            <Box classes="mg mt-20">
              <Typography exactColor={getColor("COLOR_TEXT_SECONDARY")}>
                {t("login.link.noAccount")}
              </Typography>
              <Typography
                type="subtitle2"
                exactColor={getColor("COLOR_TEXT_BRAND")}
                classes="mg ml-8 hover-text-underline"
                onClick={() => navigate(`${process.env.PUBLIC_URL}/signup`)}
              >
                {t("login.signup")}
              </Typography>
            </Box>
          )}
          <Box direction={"column"} width={360}>
            <InputField
              label={t("login.login.userId")}
              placeholder={t("login.login.userIdPlaceholder")}
              maxLength={60}
              id="userId"
              classes={classNames("mg mt-48 login-inputField", {
                ar: i18n.language.includes("ar"),
              })}
              value={userInputInfo.userId}
              onChange={(e) =>
                changeUserInputInfo({
                  ...userInputInfo,
                  userId: e.target.value,
                })
              }
              onKeyUp={(e) => e.code === "Enter" && handleLogin()}
              size={"lg"}
              isError={err !== "NONE"}
              trailingIcon={
                <Box alignItems={"center"}>
                  {userInputInfo.userId?.length > 0 && (
                    <span
                      className={"pwViewBtn"}
                      onClick={() =>
                        changeUserInputInfo({
                          ...userInputInfo,
                          userId: "",
                        })
                      }
                    >
                      <Icons
                        variant="line"
                        label="closeLine"
                        stroke={"#717985"}
                      />
                    </span>
                  )}
                </Box>
              }
              autoFocus
            />
            <InputField
              label={t("login.login.password")}
              placeholder={t("login.login.passwordPlaceholder")}
              maxLength={60}
              id="password"
              size={"lg"}
              classes={classNames("mg mt-24 login-password", {
                ar: i18n.language.includes("ar"),
              })}
              type={isPwVisible ? "text" : "password"}
              value={userInputInfo.password}
              onChange={(e) =>
                changeUserInputInfo({
                  ...userInputInfo,
                  password: e.target.value,
                })
              }
              onKeyUp={(e) => e.code === "Enter" && handleLogin()}
              trailingIcon={
                <Box alignItems={"center"}>
                  {userInputInfo.password?.length > 0 && (
                    <span
                      className={"pwViewBtn mg mr-8"}
                      onClick={() =>
                        changeUserInputInfo({
                          ...userInputInfo,
                          password: "",
                        })
                      }
                    >
                      <Icons
                        variant="line"
                        label="closeLine"
                        stroke={"#717985"}
                      />
                    </span>
                  )}
                  <Tooltip
                    message={
                      isPwVisible
                        ? t("login.login.hidePW")
                        : t("login.login.showPW")
                    }
                  >
                    <span
                      className={"pwViewBtn"}
                      onClick={() => togglePWVisible(!isPwVisible)}
                    >
                      <Icons
                        variant="line"
                        label="preview"
                        isOn={!isPwVisible}
                        stroke={"#717985"}
                      />
                    </span>
                  </Tooltip>
                </Box>
              }
              isError={err !== "NONE"}
              helpMessage={renderPWErrorStr(err)}
            />
            <Button
              classes={"mg mt-40"}
              btnStyle="fill"
              label={t("login.title")}
              style={{ height: "48px" }}
              size={"lg"}
              state={
                !!userInputInfo.password && !!userInputInfo.userId
                  ? "default"
                  : "disable"
              }
              onClick={handleLogin}
            />
            {extraOpenModalInfo === "FIND_PW" && (
              <FindPasswordModal
                open
                changeLoading={changeLoading}
                onClose={() => changeExtraOpenModalInfo("CLOSE")}
              />
            )}
            {extraOpenModalInfo === "CHANGE_PW" && (
              <ChangePasswordModal
                open
                userId={pwUserId}
                changeLoading={changeLoading}
                onClose={() => changeExtraOpenModalInfo("CLOSE")}
              />
            )}
            {extraOpenModalInfo === "MAIL_OTP" && (
              <MailOTPLoginModal
                open
                requestId={userInputInfo.userId}
                changeLoading={changeLoading}
                onClose={() => changeExtraOpenModalInfo("CLOSE")}
              />
            )}
            {extraOpenModalInfo === "GOOGLE_OTP" && (
              <GoogleOTPLoginModal
                open
                requestId={userInputInfo.userId}
                onClose={() => changeExtraOpenModalInfo("CLOSE")}
              />
            )}
            {extraOpenModalInfo === "GOOGLE_OTP_REGISTER" && (
              <GoogleOTPRegisterModal
                open
                requestId={userInputInfo.userId}
                onClose={() => changeExtraOpenModalInfo("CLOSE")}
              />
            )}
            {extraOpenModalInfo === "ESET_LOGIN" && (
              <ESETLoginModal
                open
                userId={userInputInfo.userId}
                changeLoading={changeLoading}
                onClose={() => changeExtraOpenModalInfo("CLOSE")}
              />
            )}
            {!isAdmin && (
              <Box direction="column">
                <Box
                  alignItems={"center"}
                  style={{ marginTop: 26 }}
                  justifyContent={"space-between"}
                >
                  <Checkbox
                    label={t("login.login.saveUserId")}
                    check={saveId}
                    onClick={() => changeSaveId(!saveId)}
                  />
                  <Typography
                    type={"body2_rg"}
                    classes={"login-button"}
                    exactColor={getColor("COLOR_GREEN_60")}
                    onClick={() => changeExtraOpenModalInfo("FIND_PW")}
                  >
                    {t("login.login.findPW")}
                  </Typography>
                </Box>
                <div className={"divider"} />
                <Box direction={"column"}>
                  <Box alignItems={"center"}>
                    <Typography
                      type={"body3_rg"}
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {t("login.login.noAccount")}
                    </Typography>
                    <Typography
                      type={"body3_rg"}
                      classes={"mg ml-4 login-button"}
                      exactColor={getColor("COLOR_GREEN_60")}
                      onClick={() =>
                        navigate(
                          window.eco.serviceType === "OCI"
                            ? "/signup"
                            : "/checkSignup"
                        )
                      }
                    >
                      {t("login.signup")}
                    </Typography>
                  </Box>
                  <Box alignItems={"center"} classes={"mg mt-10"}>
                    <Typography
                      type={"body3_rg"}
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {t("login.login.newECO")}
                    </Typography>
                    <Typography
                      type={"body3_rg"}
                      classes={"mg ml-4 login-button"}
                      exactColor={getColor("COLOR_GREEN_60")}
                      onClick={() =>
                        navigate(`${process.env.PUBLIC_URL}/service`)
                      }
                    >
                      {t("login.login.serviceAndDownload")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        alignItems={"center"}
        justifyContent={"center"}
        classes={"login-right"}
      >
        <img src={bgLoginImagePath} alt={"login"} height={392} />
      </Box>
    </Box>
  );
};

export default Login;
